import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import type { HealthScoreData } from '@/hooks/useRevenueIntelligence';
import { formatUiNumber, formatUiPercent } from '@/lib/ui-format';

interface HealthScoreCardProps {
  data: HealthScoreData;
}

interface TrendMeta {
  label: string;
  icon: typeof TrendingUp;
  className: string;
}

const getTrendMeta = (trend: HealthScoreData['trend']): TrendMeta => {
  const meta = {
    up: {
      label: 'Improving',
      icon: TrendingUp,
      className: 'text-status-success bg-status-success/10 border-status-success/30',
    },
    down: {
      label: 'Declining',
      icon: TrendingDown,
      className: 'text-status-danger bg-status-danger/10 border-status-danger/30',
    },
    stable: {
      label: 'Stable',
      icon: Minus,
      className: 'text-muted-foreground bg-muted border-border',
    },
  } as const;

  return meta[trend];
};

export const HealthScoreCard = ({ data }: HealthScoreCardProps): JSX.Element => {
  const trendMeta = getTrendMeta(data.trend);
  const TrendIcon = trendMeta.icon;

  return (
    <Card className="h-full">
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Pipeline Health</CardTitle>
          <Badge variant="outline" className={trendMeta.className}>
            <TrendIcon className="mr-1 h-3 w-3" />
            {trendMeta.label}
          </Badge>
        </div>
        <div className="flex items-end gap-3">
          <span className="text-3xl font-bold text-foreground">
            {formatUiNumber(data.healthScore)}
          </span>
          <span className="text-sm text-muted-foreground">/ 100</span>
          <span className="text-xs text-muted-foreground">
            {data.delta > 0 ? `+${data.delta}` : data.delta} vs last period
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress
          value={data.healthScore}
          className="h-2"
          aria-label="Pipeline health score"
          aria-valuetext={`${data.healthScore} out of 100`}
        />
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-muted-foreground">Reply Rate</p>
            <p className="font-semibold text-foreground">
              {formatUiPercent(data.metrics.replyRate / 100, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Conversion</p>
            <p className="font-semibold text-foreground">
              {formatUiPercent(data.metrics.conversionRate / 100, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Meetings</p>
            <p className="font-semibold text-foreground">
              {formatUiNumber(data.metrics.meetingsBooked)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
